import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "263",
  "slug": "how-many-countries-can-you-name-in-5-minutes",
  "title": "How Many Countries Can You Name in 5 Minutes?",
  "excerpt": "Try a timed country quiz, then use your missing answers to choose what to practise. Set consistent rules before comparing scores.",
  "tldr": "Set a five-minute timer and record as many country names as you can without looking them up. Check against an agreed list, remove duplicates, and group missed answers by region. Compare later attempts under the same rules rather than against an unsupported average.",
  "date": "2026-05-03",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1457369804613-52c61a468e7d?w=800&q=80",
  "readTime": "2 min",
  "content": [
    {
      "type": "paragraph",
      "text": "Start a five-minute timer and write as many country names as you can. Stop when it rings, even if another answer comes to mind. Save the list before checking it."
    },
    {
      "type": "paragraph",
      "text": "This is a self-test you can repeat. We do not have a documented sample that would justify calling a particular score average, and a timed naming task measures only one part of geographic knowledge."
    },
    {
      "type": "heading",
      "text": "Agree on the rules first"
    },
    {
      "type": "paragraph",
      "text": "Choose a reference list before taking the quiz. Country quizzes can use different conventions for territories and disputed states, so comparing totals without comparing the lists can be misleading."
    },
    {
      "type": "paragraph",
      "text": "Decide whether spelling variants count and whether you will type or write by hand. If you play with friends, use the same rules for everyone. Remove duplicate entries before counting the final score."
    },
    {
      "type": "heading",
      "text": "Sort the missing answers"
    },
    {
      "type": "paragraph",
      "text": "After checking your list, group the countries you missed by region. Distinguish names you recognised immediately on seeing the answer from names you did not know. The first group gives you a recall exercise; the second needs some map study."
    },
    {
      "type": "paragraph",
      "text": "Choose one small region to revisit. Locate its countries on a labelled map, then hide the labels and try naming them again. Write down neighbours as well as names so each country has a place in the group."
    },
    {
      "type": "heading",
      "text": "Try a route through the map"
    },
    {
      "type": "paragraph",
      "text": "On your next timed attempt, work through regions in an order you can remember. For example, move from North America through Central America and the Caribbean, then into South America. Use a separate section of your answer sheet for each region to reduce duplicates."
    },
    {
      "type": "paragraph",
      "text": "If you get stuck, leave space and move on. Return to that region if there is time. After the attempt, check whether the route helped you recall more names or simply changed which ones you listed first."
    },
    {
      "type": "heading",
      "text": "Check location as well as recall"
    },
    {
      "type": "paragraph",
      "text": "Pick a few countries from your answers and place them on a blank map. You may be able to recall a name quickly without knowing where it belongs. Keep that result separate from the timed naming score."
    },
    {
      "type": "paragraph",
      "text": "For a later attempt, keep the time limit and reference list unchanged. Compare which countries you missed, not only the total. That gives you a concrete choice about what to practise next."
    }
  ]
};
